import {
  PERFORMER_KINDS,
  PERFORMER_KIND_WORDS,
  TRADE_GROUP_WORDS,
  csiDivision,
  inTradeOrder,
  tradeGroups,
  type PerformerDto,
  type PerformerKind,
  type TradeDto,
  type TradesDto,
} from '@nsb/core';
import { useState } from 'react';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { Button, ErrorLine, LoadError, Loading, Page, Section, TextInput, Title } from '../../components/ui.tsx';
import { useUnsaved } from '../../components/useUnsaved.ts';
import { isAdmin, useMe } from '../../session.tsx';
import { changedLabels } from '../../unsaved.ts';

/**
 * Settings → Trades and subs (tech-tree spec v2 §4): the org's trades, the CSI
 * trades table Jason gave in the order of its numbers, and the performers who
 * do the work: NSB itself, each sub, supplier and consultant, shelved under
 * their trades. A package on a job names one trade and one performer from
 * here. The admin keeps a quick list of key trades, and those are the trades a
 * sub is given (Jason, 2026-10-04: "the csi standard should be the number
 * scheme ... my key trades are just a selection there of unless I define a
 * unique trade specifically, like natural plaster for example"). Every trade
 * has a painted picture, and every list of them here is NSB's own first, then
 * the key trades, then the rest, each by its number (core `tradeGroups`; L-15,
 * Jason, 2026-10-07: "paint icons for all the trades, put organizational
 * custom trades at the top, then key trades then the rest").
 */
export function TradesSettings() {
  const { data: me } = useMe();
  const { data, error } = useApi<TradesDto>(['trades'], '/trades');
  const admin = isAdmin(me);
  useFeedbackRecord(null, { type: 'trades', id: 'org', label: 'Trades and subs' });
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const live = data.trades.filter((t) => !t.archived);
  const key = inTradeOrder(live.filter((t) => t.keyTrade));
  const subs = data.performers.filter((p) => !p.archived && p.kind !== 'nsb');
  return (
    <Page wide>
      <Title kicker="Settings · trades and subs">Who does the work</Title>
      <p className="mb-8 max-w-3xl font-display text-[1.25rem] leading-snug">
        {key.length} key trades, the quick list a sub is given, of the {live.length} in the CSI trades table and NSB’s own.{' '}
        {subs.length === 0 ? 'No subs or suppliers yet: NSB does everything until someone else is named.' : `${subs.length} ${subs.length === 1 ? 'performer' : 'performers'} besides NSB.`}
      </p>
      <div className="grid gap-x-16 2xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] 2xl:items-start">
        <Section title="Key trades">
          <p className="mb-3 max-w-measure text-sm text-ink-2">The trades NSB works with most, NSB’s own first: these are the trades a sub, supplier or consultant is given. Mark any trade in the list below as key.</p>
          <KeyTrades trades={key} />
        </Section>
        <Section title="Performers">
          <p className="mb-3 max-w-measure text-sm text-ink-2">NSB, and every sub, supplier and consultant, with the key trades they do.</p>
          <PerformerList performers={data.performers} trades={live} admin={admin} />
        </Section>
      </div>
      <Section title="Every trade">
        <p className="mb-3 max-w-measure text-sm text-ink-2">
          NSB’s own trades first, marked Custom, then the key trades, then the rest of the CSI trades table by division, every row with the table’s own number and name and a painted picture. The number sets where a trade’s lines print on the schedule of values; it is never a key.
        </p>
        <Pictures pictures={data.pictures} admin={admin} />
        <TradeList trades={data.trades} admin={admin} />
      </Section>
    </Page>
  );
}

const refresh = () => queryClient.invalidateQueries({ queryKey: ['trades'] });

/** A trade of NSB's own, not a row of the CSI trades table: said in a word wherever it is listed. */
function CustomMark({ trade }: { trade: TradeDto }) {
  return trade.custom ? <span className="label shrink-0 !text-cedar">Custom</span> : null;
}

/** A trade's picture, or nothing: never an empty frame. */
function TradePicture({ trade, size }: { trade: TradeDto; size: number }) {
  if (!trade.icon) return <span aria-hidden="true" className="inline-block shrink-0" style={{ width: size, height: size }} />;
  return <img src={trade.icon} alt="" className="shrink-0 object-contain" style={{ width: size, height: size }} />;
}

function KeyTrades({ trades }: { trades: TradeDto[] }) {
  if (!trades.length) return <p className="text-ink-3">No key trades yet: mark the ones NSB works with most in the list below.</p>;
  return (
    <ul className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
      {trades.map((t) => (
        <li key={t.id} className="flex min-h-14 items-center gap-2.5">
          <TradePicture trade={t} size={44} />
          <span className="min-w-0 text-sm">
            <span className="flex items-baseline gap-2">
              <span className="num text-[0.75rem] text-ink-3">{t.csi ?? '—'}</span>
              <CustomMark trade={t} />
            </span>
            {t.name}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The trades' pictures: how many are being painted, and painting the rest (an OpenAI image each), for an admin to start. */
function Pictures({ pictures: p, admin }: { pictures: TradesDto['pictures']; admin: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const paint = async () => {
    setBusy(true);
    setErr(null);
    try {
      queryClient.setQueryData(['trades'], await api<TradesDto>('/trades/icons', { body: {} }));
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!p.painting && !p.missing) return null;
  return (
    <div className="mb-4 text-sm text-ink-2">
      {p.painting ? <p className="motion-safe:animate-pulse">{p.painting} being painted now.</p> : null}
      {p.missing && !p.painting ? (
        <p>
          {p.missing} {p.missing === 1 ? 'trade has' : 'trades have'} no picture yet.{' '}
          {admin ? (
            p.blocked ? (
              <span className="text-ink-3">{p.blocked}</span>
            ) : (
              <Button tone="quiet" busy={busy} onClick={() => void paint()}>
                Paint {p.missing === 1 ? 'it' : `the ${p.missing}`}
              </Button>
            )
          ) : null}
        </p>
      ) : null}
      <ErrorLine error={err} />
    </div>
  );
}

/** The rest of the trades by their CSI division, a trade with no number last. */
function byDivision(trades: readonly TradeDto[]): [string, TradeDto[]][] {
  const divisions = new Map<string, TradeDto[]>();
  for (const t of trades) {
    const d = t.csi?.slice(0, 2) ?? '';
    divisions.set(d, [...(divisions.get(d) ?? []), t]);
  }
  return [...divisions];
}

/**
 * Every trade: NSB's own, then the key trades, then the rest by CSI division (core `tradeGroups`); and a trade of
 * NSB's own, added under its number.
 */
function TradeList({ trades, admin }: { trades: TradeDto[]; admin: boolean }) {
  const [showArchived, setShowArchived] = useState(false);
  const [name, setName] = useState('');
  const [csi, setCsi] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const shown = trades.filter((t) => showArchived || !t.archived);
  const archived = trades.filter((t) => t.archived).length;
  const add = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api('/trades', { body: { name: name.trim(), csi: csi.trim() || null } });
      setName('');
      setCsi('');
      await refresh();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-4xl pt-3">
      {tradeGroups(shown).map((g) => (
        <section key={g.group} className="mb-8">
          <h3 className="mb-1 border-b border-rule-strong pb-1.5 font-display text-[1.25rem]">
            {TRADE_GROUP_WORDS[g.group]} <span className="num text-[0.875rem] text-ink-3">· {g.trades.length}</span>
          </h3>
          {g.group === 'rest' ? (
            byDivision(g.trades).map(([division, list]) => (
              <div key={division || 'none'} className="mt-3">
                <p className="label border-b border-rule pb-1">
                  {division ? `Division ${division} · ${csiDivision(division)?.name ?? ''}` : 'No CSI number'} · {list.length}
                </p>
                <TradeRows trades={list} admin={admin} />
              </div>
            ))
          ) : (
            <TradeRows trades={g.trades} admin={admin} />
          )}
        </section>
      ))}
      {archived ? (
        <button type="button" className="link mt-2 min-h-9 text-sm text-ink-3 hover:text-ink" onClick={() => setShowArchived(!showArchived)}>
          {showArchived ? 'Hide' : 'Show'} {archived} archived
        </button>
      ) : null}
      {admin ? (
        <form
          className="mt-6 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void add();
          }}
        >
          <p className="w-full max-w-measure text-sm text-ink-2">A trade of NSB’s own, one the table has no row for, under the number of the division or section it belongs to.</p>
          <label className="block w-28">
            <span className="label block">CSI</span>
            <TextInput value={csi} onChange={(e) => setCsi(e.target.value)} placeholder="Five digits" />
          </label>
          <label className="block min-w-64 flex-1">
            <span className="label block">A trade of NSB’s own</span>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <Button type="submit" tone="secondary" busy={busy} disabled={!name.trim()}>
            Add it
          </Button>
          <ErrorLine error={err} />
        </form>
      ) : null}
    </div>
  );
}

function TradeRows({ trades, admin }: { trades: readonly TradeDto[]; admin: boolean }) {
  return (
    <ol className="divide-y divide-rule">
      {trades.map((t) => (
        <TradeRow key={t.id} trade={t} admin={admin} />
      ))}
    </ol>
  );
}

function TradeRow({ trade: t, admin }: { trade: TradeDto; admin: boolean }) {
  const [name, setName] = useState(t.name);
  const [csi, setCsi] = useState(t.csi ?? '');
  const [err, setErr] = useState<string | null>(null);
  const save = async (patch: Record<string, unknown>) => {
    setErr(null);
    try {
      await api(`/trades/${t.id}`, { method: 'PATCH', body: patch });
      await refresh();
    } catch (e) {
      setErr(errorText(e));
    }
  };
  // A row of the table keeps the table's number and name; only NSB's own trades are renamed or renumbered.
  const editable = admin && t.custom;
  // On a phone the picture sits beside the number, the name and its switches, one under the other; wider, one line.
  return (
    <li className={`grid grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-x-3 gap-y-0.5 py-2 sm:grid-cols-[2.75rem_6.5rem_minmax(0,1fr)_5rem_6rem] sm:gap-x-4 sm:gap-y-1 sm:py-1.5 ${t.archived ? 'opacity-60' : ''}`}>
      <span className="row-span-3 self-start pt-0.5 sm:row-span-1 sm:self-center sm:pt-0">
        <TradePicture trade={t} size={36} />
      </span>
      {admin ? (
        <>
          {editable ? (
            <input
              className="field num w-28 py-1 text-sm sm:w-full"
              aria-label={`CSI number for ${t.name}`}
              value={csi}
              placeholder="—"
              onChange={(e) => setCsi(e.target.value)}
              onBlur={() => csi.trim() !== (t.csi ?? '') && void save({ csi: csi.trim() || null })}
            />
          ) : (
            <span className="num text-sm text-ink-2">{t.csi ?? '—'}</span>
          )}
          <span className="flex min-w-0 items-center gap-2">
            {editable ? (
              <input className="field min-w-0 flex-1 py-1 text-sm" aria-label="Trade name" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name.trim() !== t.name && void save({ name: name.trim() })} />
            ) : (
              <span className="min-w-0">{t.name}</span>
            )}
            <CustomMark trade={t} />
          </span>
          <span className="flex items-center gap-8 sm:contents">
            <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-9">
              <input type="checkbox" checked={t.keyTrade} onChange={(e) => void save({ keyTrade: e.target.checked })} />
              Key
            </label>
            <button type="button" className="link min-h-11 text-left text-sm text-ink-3 hover:text-ink sm:min-h-9" onClick={() => void save({ archived: !t.archived })}>
              {t.archived ? 'Bring back' : 'Archive'}
            </button>
          </span>
        </>
      ) : (
        <>
          <span className="num text-sm text-ink-2">{t.csi ?? '—'}</span>
          <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0">{t.name}</span>
            <CustomMark trade={t} />
          </span>
          <span className="flex gap-4 sm:contents">
            <span className="meta">{t.keyTrade ? 'Key' : ''}</span>
            <span className="meta">{t.archived ? 'Archived' : ''}</span>
          </span>
        </>
      )}
      {err ? (
        <span className="col-start-2 sm:col-span-3">
          <ErrorLine error={err} />
        </span>
      ) : null}
    </li>
  );
}

function PerformerList({ performers, trades, admin }: { performers: PerformerDto[]; trades: TradeDto[]; admin: boolean }) {
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const shown = performers.filter((p) => showArchived || !p.archived);
  const archived = performers.filter((p) => p.archived).length;
  return (
    <div>
      <ul className="divide-y divide-rule border-y border-rule">
        {shown.map((p) => (
          <PerformerRow key={p.id} performer={p} trades={trades} admin={admin} />
        ))}
      </ul>
      {archived ? (
        <button type="button" className="link mt-2 min-h-9 text-sm text-ink-3 hover:text-ink" onClick={() => setShowArchived(!showArchived)}>
          {showArchived ? 'Hide' : 'Show'} {archived} archived
        </button>
      ) : null}
      {admin ? (
        adding ? (
          // The form is a row of the list (an `li`), so it sits in one: never a stray bullet beside it.
          <ul>
            <PerformerForm trades={trades} onDone={() => setAdding(false)} />
          </ul>
        ) : (
          <div className="mt-4">
            <Button tone="secondary" onClick={() => setAdding(true)}>
              Add a sub, supplier or consultant
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}

function PerformerRow({ performer: p, trades, admin }: { performer: PerformerDto; trades: TradeDto[]; admin: boolean }) {
  const [editing, setEditing] = useState(false);
  // Their trades in the list's order; one no longer on the list by its key, last.
  const theirs = [...inTradeOrder(trades.filter((t) => p.trades.includes(t.key))).map((t) => t.name), ...p.trades.filter((k) => !trades.some((t) => t.key === k))];
  if (editing) return <PerformerForm trades={trades} performer={p} onDone={() => setEditing(false)} />;
  return (
    <li className={`flex flex-wrap items-baseline gap-x-4 gap-y-1 py-3 ${p.archived ? 'opacity-60' : ''}`}>
      <span className="min-w-48 font-display text-[1.125rem]">{p.name}</span>
      <span className="label">{PERFORMER_KIND_WORDS[p.kind]}</span>
      <span className="min-w-0 flex-1 text-sm text-ink-2">{p.kind === 'nsb' ? 'Every trade, unless someone else is named' : theirs.length ? theirs.join(', ') : 'No trades listed yet'}</span>
      {p.email || p.phone ? <span className="meta">{[p.email, p.phone].filter(Boolean).join(' · ')}</span> : null}
      {p.archived ? <span className="meta">Archived</span> : null}
      {admin ? (
        <button type="button" className="link min-h-9 text-sm text-ink-3 hover:text-ink" onClick={() => setEditing(true)}>
          Edit
        </button>
      ) : null}
    </li>
  );
}

function PerformerForm({ trades, performer: p, onDone }: { trades: TradeDto[]; performer?: PerformerDto; onDone: () => void }) {
  const [name, setName] = useState(p?.name ?? '');
  const [kind, setKind] = useState<PerformerKind>(p?.kind ?? 'sub');
  const [picked, setPicked] = useState<Set<string>>(() => new Set(p?.trades ?? []));
  const [email, setEmail] = useState(p?.email ?? '');
  const [phone, setPhone] = useState(p?.phone ?? '');
  const [notes, setNotes] = useState(p?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const nsb = p?.kind === 'nsb';
  // The key trades, and any other this performer already has (so it can be taken off), in the list's order.
  const offered = inTradeOrder(trades.filter((t) => t.keyTrade || picked.has(t.key) || p?.trades.includes(t.key)));
  const save = async (archived?: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      const body = { name: name.trim(), kind, trades: [...picked], email: email.trim() || null, phone: phone.trim() || null, notes: notes.trim() || null, ...(archived !== undefined ? { archived } : {}) };
      if (p) await api(`/performers/${p.id}`, { method: 'PATCH', body });
      else await api('/performers', { body });
      await refresh();
      onDone();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  // Someone's details being changed (Save), changed and not saved, on the bar at the window's foot (P-29). Someone being
  // added is a new thing made, as Add them says, not a change to what is there.
  const now = { name, kind, trades: [...picked].sort(), email: email.toLowerCase(), phone, notes };
  useUnsaved({
    names: p
      ? changedLabels({ name: p.name, kind: p.kind, trades: [...p.trades].sort(), email: p.email, phone: p.phone, notes: p.notes }, now, [
          ['Name', (x) => x.name],
          ['Kind', (x) => x.kind],
          ['Email', (x) => x.email],
          ['Phone', (x) => x.phone],
          ['Trades they do', (x) => x.trades],
          ['Notes', (x) => x.notes],
        ])
      : [],
    busy,
    canSave: Boolean(name.trim()),
    error: err,
    onSave: () => save(),
    onDiscard: () => {
      if (!p) return;
      setName(p.name);
      setKind(p.kind);
      setPicked(new Set(p.trades));
      setEmail(p.email ?? '');
      setPhone(p.phone ?? '');
      setNotes(p.notes ?? '');
      setErr(null);
    },
  });
  return (
    <li className="py-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="label block">Name</span>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Who they are" />
        </label>
        <label className="block">
          <span className="label block">Kind</span>
          <select className="field" value={kind} disabled={nsb} onChange={(e) => setKind(e.target.value as PerformerKind)}>
            {PERFORMER_KINDS.filter((k) => k !== 'nsb' || nsb).map((k) => (
              <option key={k} value={k}>
                {PERFORMER_KIND_WORDS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label block">Email</span>
          <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="block">
          <span className="label block">Phone</span>
          <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
        </label>
      </div>
      {!nsb ? (
        <fieldset className="mt-3">
          <legend className="label mb-1">Trades they do</legend>
          {offered.length ? (
            <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
              {offered.map((t) => (
                <label key={t.key} className="flex min-h-11 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={picked.has(t.key)}
                    onChange={(e) => {
                      const next = new Set(picked);
                      if (e.target.checked) next.add(t.key);
                      else next.delete(t.key);
                      setPicked(next);
                    }}
                  />
                  <TradePicture trade={t} size={28} />
                  {t.name}
                  <CustomMark trade={t} />
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink-3">No key trades yet: mark the ones NSB works with most in the list of every trade.</p>
          )}
        </fieldset>
      ) : null}
      <label className="mt-3 block">
        <span className="label block">Notes</span>
        <textarea className="field resize-y text-sm" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="NSB only: how they work, what they charge, who to call" />
      </label>
      <div className="mt-3 flex flex-wrap gap-4">
        <Button busy={busy} disabled={!name.trim()} onClick={() => void save()}>
          {p ? 'Save' : 'Add them'}
        </Button>
        <Button tone="quiet" onClick={onDone}>
          Cancel
        </Button>
        {p && !nsb ? (
          <Button tone="quiet" busy={busy} onClick={() => void save(!p.archived)}>
            {p.archived ? 'Bring back' : 'Archive'}
          </Button>
        ) : null}
      </div>
      <ErrorLine error={err} />
    </li>
  );
}
